/**
 * React dependencies
 */
import { useRef, useState } from 'react';

/**
 * DWT dependencies
 */
import AudioPlayer from '../AudioPlayer/AudioPlayer';
import { ModalContext } from '../Modal/ModalContext';
import useModal from '../../hooks/useModal';
import Modal from '../Modal/Modal';

/**
 * Featured Book Component
 *
 * Component that renders either an audio player or regular book view
 * based on global book cover style settings.
 * This is for the book cover and the modal only. The text section is not a React component.
 *
 */
const FeaturedBookComponent = ({ bookData }) => {
  const [ modalOpen, setModalOpen ] = useState( false );
  const modalRef = useRef( null );

  // Check global book cover style setting
  const bookCoverStyle = window?.DP?.book_cover_background;
  const isAudio = bookCoverStyle === 'Audio' || bookData.testFormat === 'Audio';

  // Context for modal functionality
  const context = {
    featureBy: 'books',
    allowedMetadataFields: JSON.stringify(
      window.DP?.metadata_fields
        ? window.DP?.metadata_fields
        : [ 'Title', 'Contributor Name', 'Format' ]
    ),
    entry: 'manual',
    module: 'featured_book',
  };

  // Create book data for modal (using apiImage field name for modal compatibility)
  const modalBookData = {
    isbn: bookData.isbn,
    title: bookData.title,
    contributors: bookData.contributors,
    workId: bookData.workId,
    audioUrl: bookData.audioUrl,
    audioContributors: bookData.audioContributors,
    description: bookData.description || '',
    format: bookData.format,
    apiImage: bookData.coverImage,
    altText: bookData.altText,
  };

  const {
    modalClass,
    modalData,
    modalType,
    onClickOpenModal,
    onClickCloseModal
  } = useModal( [ modalBookData ], modalRef, context );

  const handleModalOpen = ( isbn, type ) => {
    setModalOpen( true );
    onClickOpenModal( isbn, type );
  };

  if ( isAudio ) {
    // Render audio player
    return (
      <>
        <ModalContext.Provider value={{ onClickOpenModal: handleModalOpen }}>
          <div className="featured-book-audio-player">
            <AudioPlayer
              item={{
                isbn: bookData.isbn,
                title: bookData.title,
                audioUrl: bookData.audioUrl,
                audioContributors: bookData.audioContributors,
                workId: bookData.workId
              }}
              altText={bookData.altText}
              format={bookData.format}
              coverUrl={bookData.coverImage}
              context={{ entry: 'manual' }}
            />
          </div>
        </ModalContext.Provider>
        {modalOpen && (
          <Modal
            context={context}
            modalData={modalData}
            modalClass={modalClass}
            modalType={modalType}
            onClose={() => {
              setModalOpen( false );
              onClickCloseModal();
            }}
            ref={modalRef}
          />
        )}
      </>
    );
  } else {
    // Render regular book view
    return (
      <>
        <div className="image-wrapper is-regular-book">
          <div className="api-image-wrapper featured-book with-hover">
            <img
              className="api-image"
              src={bookData.coverImage}
              alt={bookData.altText}
              data-isbn={bookData.isbn}
              data-workid={bookData.workId}
              data-format={bookData.format}
              data-audio-url={bookData.audioUrl || ''}
              loading="lazy"
            />
          </div>

          <div className="overlay"></div>
          <button
            className="featured-book__view-details"
            onClick={() => handleModalOpen( bookData.isbn, 'books' )}
          >
            +View Details
          </button>
        </div>
        {modalOpen && (
          <Modal
            context={context}
            modalData={modalData}
            modalClass={modalClass}
            modalType={modalType}
            onClose={() => {
              setModalOpen( false );
              onClickCloseModal();
            }}
            ref={modalRef}
          />
        )}
      </>
    );
  }
};

export default FeaturedBookComponent;
